import { meseeknaApi } from "./meseeknaApi"

export type LoginPlainRequest = {
  username: string
  password: string
}

export type LoginPlainResponse = {
  status: string
  message: string
  code: number
  data: [
    {
      id: number
      firstName: string
      lastName: string
      email: string
      organizationId: number
      role: number
      imageUrl: string
      createdOn: string
      userName: string
      status: number
      expire_status: number
      roleName: string
      organizationName: string
      roles: {
        roleId: number
        name: string | null
      }[]
      userHash: string
      token: string
    },
  ]
}

export type ChangePasswordRequest = {
  email: string
  currentPassword: string
  newPassword: string
}

export type ChangePasswordResponse = {
  status?: string
  message?: string
  code?: number
}

export type CreateUserRequest = {
  firstName: string
  lastName: string
  email: string
  role: number
}

export type CreateUserResponse = {
  status?: string
  message?: string
  code?: number
  data?: {
    id: number
    firstName: string
    lastName: string
    email: string
    organizationId: number
    roles: Array<{
      roleId: number
      name: string
    }>
    password?: string // For auto-generate option, if returned by API
  }
}

// CSV Import types - uses v2 endpoint that properly sends invitation emails via Postmark
export type CsvImportResponse = {
  status: string
  code: number
  insertedMail: Array<{
    firstName: string
    lastName: string
    email: string
    role: number
  }>
  errorData: Array<{
    firstName: string
    lastName: string
    email: string
    role: string
    remarks: string
  }>
  insertedCount: string
  message: string
}

export type AdminCreateUserRequest = {
  email: string
  organizationId: number
  firstName?: string
  lastName?: string
  password?: string
  role?: number
  sendEmail?: boolean
  linkType?: "login" | "onboarding"
}

export type AdminCreateUserResponse = {
  status: string
  data: {
    id: number
    email: string
    organizationId: number
    roles: Array<{
      roleId: number
      name: string
    }>
    otpToken: string
    otpLink: string
    linkType: string
  }
}

export type AdminOrganization = {
  id: number
  organizationName: string
  onboardingAppointmentScheduled: boolean | null
  onboardingCompleted: boolean
  onboardingCompletedAt: Date | null
  onboardingCompletedBy: number | null
  createdOn: Date
  isManuallyPaid: boolean
  subscriptionStatus: string | null
  _count: {
    users: number
  }
}

export type AdminOrganizationsResponse = {
  status: string
  code: number
  data: AdminOrganization[]
}

export type AdminTest = {
  id: number
  title: string
  shortName: string
  active: string
  dateImported?: string
  manualDescription?: string | null
  simType?: "business" | "geopolitical" | "custom" | "not_assigned"
  forDemo?: string | null
  pdfURL?: string | null
  videoURL?: string | null
  imageData?: string | null // Data URI or SVG code stored directly in DB
  status?: number | null
  contents?: string | null
}

export type AdminTestsResponse = {
  status: string
  code: number
  data: AdminTest[]
}

export type AdminTestResponse = {
  status: string
  code: number
  data: AdminTest
}

export type CreateTestRequest = {
  shortName: string
  title: string
  contents?: string
  manualDescription?: string
  simType?: "business" | "geopolitical" | "custom" | "not_assigned"
  active?: "Y" | "N"
  forDemo?: "Y" | "N"
  pdfURL?: string
  videoURL?: string
  imageData?: string // Data URI or SVG code
}

export type UpdateTestRequest = {
  shortName?: string
  title?: string
  contents?: string
  manualDescription?: string
  simType?: "business" | "geopolitical" | "custom" | "not_assigned"
  active?: "Y" | "N"
  forDemo?: "Y" | "N"
  pdfURL?: string | null
  videoURL?: string | null
  imageData?: string | null // Data URI or SVG code
}

export type DeleteTestResponse = {
  status: string
  code: number
  message: string
}

export type SimulationAssignmentRequest = {
  userId: number
}

export type SimulationAssignmentResponse = {
  status: string
  code: number
  message: string
  data: {
    id: number
    testId: number
    organizationId: number
    userId: number
    dateAssigned: string
    dateCompleted: string | null
  }
}

export type CreateOrganizationRequest = {
  organizationName: string
}

export type CreateOrganizationResponse = {
  status: string
  data: {
    id: number
    organizationName: string
    status: number
    createdOn: string
  }
}

export type UpdateOrganizationRequest = {
  organizationName: string
}

export type UpdateOrganizationResponse = {
  status: string
  data: {
    id: number
    organizationName: string
    status: number
    createdOn: string
  }
}

export type OrganizationSimulation = {
  id: number
  testId: number
  organizationId: number
  userId: number
  dateAssigned: string
  dateCompleted: string | null
  simulation: {
    id: number
    title: string
    shortName: string
    active: string
    contents: string | null
  }
}

export type OrganizationSimulationsResponse = {
  status: string
  code: number
  message: string
  data: OrganizationSimulation[]
}

// ==================== COMPOSITE SCORE TYPES ====================

export type CompositeScoreSet = {
  id: number
  name: string | null
  organizationId: number | null
  createdOn: string | null
  tier?: string // JSON string from API
  tiers?: CompositeScoreTier[] // Parsed tiers
}

export type CompositeScoreTier = {
  id: number
  name: string | null
  color: string | null
  output_score: number | null
  composite_score_set_id: number | null
}

export type CompositeScoreBucket = {
  id: number
  min_score: number | null
  max_score: number | null
  composite_score_tier_id: number | null
  measure_id: number | null
  measureCode?: string | null
  measureName?: string | null
}

export type CompositeScoreSetsResponse = {
  status: string
  code: number
  data: CompositeScoreSet[]
}

export type CompositeScoreTiersResponse = {
  status: string
  code: number
  data: CompositeScoreTier[]
}

export type CompositeScoreBucketsResponse = {
  status: string
  code: number
  data: CompositeScoreBucket[]
}

export type UpdateCompositeScoreBucketRequest = {
  id: number
  min_score: number
  max_score: number
}

export type CompositeScoreOrgMapping = {
  organizationId: number
  organizationName: string
}

export type CompositeScoreOrgMappingsResponse = {
  status: string
  code: number
  data: CompositeScoreOrgMapping[]
}

// Score Set CRUD Types
export type CreateCompositeScoreSetRequest = {
  name: string
  organizationId?: number | null
}

export type UpdateCompositeScoreSetRequest = {
  id: number
  name: string
}

// Tier CRUD Types
export type CreateCompositeScoreTierRequest = {
  name: string
  color: string
  composite_score_set_id: number
  output_score: number
}

export type UpdateCompositeScoreTierRequest = {
  id: number
  name: string
  color: string
  output_score: number
}

// Bucket CRUD Types
export type CreateCompositeScoreBucketRequest = {
  min_score: number
  max_score: number
  measure_id: number
  composite_score_tier_id: number
}

// Org Mapping Types
export type UpdateCompositeScoreOrgMappingRequest = {
  composite_score_set_id: number
  orgIdsArray: number[]
}

export type ForgotPasswordRequest = {
  email: string
}

export type ForgotPasswordResponse = {
  status: string
  code: number
  message: string
}

export type ResetPasswordRequest = {
  token: string
  email: string
  password: string
}

export type ResetPasswordResponse = {
  status: string
  code: number
  message: string
}

export type UserProfileResponse = {
  status: "success" | "fail"
  code: number
  data?: UserProfile
  message?: string
}

export type UserProfile = {
  id: number
  email: string
  firstName?: string
  lastName?: string
  roles: Array<{
    roleId: number
    name: string | null
  }>
  organizationId: number | null
  organization: {
    id: number
    name: string
    onboardingCompleted: boolean
    onboardingAppointmentScheduled: boolean
  } | null
  subscription: {
    id: number
    status: string
    seats: Array<{
      id: number
      role: {
        id: number
        name: string
      }
      type: string
      status: string
    }>
  } | null
}

// Organization onboarding completion response type
export type OnboardingCompletionResponse = {
  status: string
  code: number
  data: {
    organizationId: number
    organizationName: string
    onboardingAppointmentScheduled: boolean
    onboardingCompleted: boolean
    onboardingCompletedAt: string | null
    onboardingCompletedBy: number | null
    selectedScenario: string | null
  }
}

// Update onboarding completion request type
export type UpdateOnboardingCompletionRequest = {
  onboardingCompleted: boolean
}

// Request type for updating onboarding appointment status
export type UpdateOnboardingAppointmentRequest = {
  organizationId: string | number
  scheduled: boolean
}

// Response type for updating onboarding appointment status
export type UpdateOnboardingAppointmentResponse = {
  status?: string
  message?: string
  code?: number
}

// Response type for updating selected scenario
export type UpdateSelectedScenarioResponse = {
  status: string
  code: number
  data: {
    organizationId: number
    organizationName: string
    onboardingAppointmentScheduled: boolean
    onboardingCompleted: boolean
    onboardingCompletedAt: string | null
    onboardingCompletedBy: number | null
    selectedScenario: string | null
  }
}

// Organization measure types
export type OrganizationMeasure = {
  id: number
  organizationId: number
  measureId: number
  isActive: boolean
  customValue?: number | null
  customTarget?: number | null
  createdOn: string | null
  updatedOn: string | null
  createdBy?: number | null
  updatedBy?: number | null
  organization: {
    id: number
    organizationName: string
  }
  measure: {
    id: number
    measureName: string
    measureCode?: string | null
    description?: string | null
  }
}

export type GetOrganizationMeasuresResponse = {
  status: string
  code: number
  message?: string
  data: OrganizationMeasure[]
}

// Measure types for getMeasures endpoint
export type Measure = {
  id: number
  measureName: string
  measureCode: string | null
  description: string | null
  category: string | null
  measureColor: string | null
  isActive: boolean
  createdOn: string | null
}

export type MeasuresResponse = {
  status: string
  code: number
  message?: string
  data: Measure[]
}

// Types for getAllMeasures with options
export type GetAllMeasuresParams = {
  includeInactive?: boolean
  withCounts?: boolean
}

// Extended measure type with counts (when withCounts=true)
export type MeasureWithCounts = Measure & {
  scenarioCount?: number
  journalPromptCount?: number
  organizationCount?: number
  microlearningUsers?: number
  runCalculations?: number
}

export type MeasuresWithCountsResponse = {
  status: string
  code: number
  message?: string
  data: MeasureWithCounts[]
}

// Types for soft delete/restore measure
export type SoftDeleteMeasureRequest = {
  measureId: number
}

export type SoftDeleteMeasureResponse = {
  status: string
  code: number
  message?: string
  data: Measure
}

export type RestoreMeasureRequest = {
  measureId: number
}

export type RestoreMeasureResponse = {
  status: string
  code: number
  message?: string
  data: Measure
}

export type CreateOrganizationMeasureRequest = {
  measureId: number
  isActive?: boolean
  customValue?: number | null
  customTarget?: number | null
}

export type CreateOrganizationMeasureResponse = {
  status: string
  code: number
  message?: string
  data: OrganizationMeasure
}

export type UpdateOrganizationMeasureRequest = {
  isActive?: boolean
  customValue?: number | null
  customTarget?: number | null
}

export type UpdateOrganizationMeasureResponse = {
  status: string
  code: number
  message?: string
  data: OrganizationMeasure
}

export type DeleteOrganizationMeasureResponse = {
  status: string
  code: number
  message: string
}

export type RefreshSessionResponse = {
  status: string
  code: number
  message: string
}

export type CreateTeamSimulationRequest = {
  scenarioId: number
  name: string
  participantEmails: string[]
  organizationId: number
  createdBy?: number
  scheduledStartTime?: string
  timezone?: string
}

export type CreateTeamSimulationResponse = {
  status: string
  code: number
  message: string
  teamSimulationId: string
  passcode: string
}

export type SendTeamInvitationsRequest = {
  teamSimulationId: string
  simulationName: string
  scenarioName: string
  adminName: string
  participantEmails: string[]
  organizationId: number
  scheduledStartTime?: string
  timezone?: string
  customEmailSubject?: string
  customEmailBody?: string
}

export type SendTeamInvitationsResponse = {
  status: string
  code: number
  message: string
  emailsSent: number
  failedEmails: string[]
}

export type LoginWithPasscodeRequest = {
  email: string
  passcode: string
  teamSimulationId: string
}

export type LoginWithPasscodeResponse = {
  status: string
  code: number
  message: string
  token: string
  userData: {
    id: number
    firstName: string
    lastName: string
    email: string
    teamSimulationId: string
  }
}

export type GetTeamSimulationDetailsResponse = {
  status: string
  code: number
  message: string
  data: {
    id: string
    name: string
    scenarioId: number
    organizationId: number
    createdBy: number
    participants: Array<{
      id: number
      email: string
      firstName?: string
      lastName?: string
      status?: string
    }>
    scheduledStartTime: string | null
    timezone: string | null
    createdAt: string
    startedAt: string | null
    endedAt: string | null
    status: string
  }
}

export type AutoStartTeamSimulationResponse = {
  status: 'success' | 'pending' | 'fail'
  code: number
  message: string
  data: {
    id: string
    status: string
    startedAt?: string | null
    scheduledStartTime?: string | null
  } | null
}

export type AdminUser = {
  id: number
  email: string
  firstName: string | null
  lastName: string | null
  role: number
  status: number
  createdOn: string
  organizationId: number | null
  organization: {
    id: number
    organizationName: string
  } | null
  roles: Array<{
    roleId: number
    name: string
  }>
}

export type AdminUsersResponse = {
  status: string
  code: number
  data: AdminUser[]
}

export type AdminUpdateUserRequest = {
  id: number
  firstName?: string
  lastName?: string
  email?: string
  role?: number
  organizationId?: number
  status?: number
}

export type AdminUpdateUserResponse = {
  status: string
  code: string
  message: string
  data?: AdminUser
}

export type AdminUpdateUserPasswordRequest = {
  userId: number
  newPassword: string
}

export type AdminUpdateUserPasswordResponse = {
  status: string
  code: number
  message: string
}

// Admin Dashboard Types
export type AdminOverviewStats = {
  totalOrganizations: number
  activeSubscriptions: number
  totalUsers: number
  activeIndividualSimulations: number
  activeTeamSimulations: number
  pendingApprovals: number
  completedToday: number
}

export type AdminOverviewStatsResponse = {
  status: string
  code: number
  data: AdminOverviewStats
}

export type AdminLiveSummary = {
  individualActive: number
  teamSessionsActive: number
  collaborationRoomsActive: number
  completedToday: number
}

export type AdminLiveSummaryResponse = {
  status: string
  code: number
  data: AdminLiveSummary
}

export type ActiveIndividualSimulation = {
  id: number
  userId: number
  userFirstName: string | null
  userLastName: string | null
  userEmail: string
  organizationId: number
  organizationName: string
  testId: number
  testTitle: string
  testShortName: string | null
  startTime: string
  durationMinutes: number
}

export type AdminActiveIndividualSimulationsResponse = {
  status: string
  code: number
  data: ActiveIndividualSimulation[]
}

export type ActiveTeamSession = {
  id: string
  name: string
  organizationId: number
  organizationName: string
  scenarioId: number
  scenarioName: string | null
  status: string
  participantCount: number
  createdAt: string
  createdByEmail: string | null
}

export type AdminActiveTeamSessionsResponse = {
  status: string
  code: number
  data: ActiveTeamSession[]
}

export type RecentActivity = {
  id: string
  type: "simulation_started" | "simulation_completed" | "team_session_created" | "team_session_completed"
  userId?: number
  userName?: string
  organizationName?: string
  simulationName?: string
  timestamp: string
}

export type AdminRecentActivityResponse = {
  status: string
  code: number
  data: RecentActivity[]
}

// Pending Approval Simulation Types
export type PendingApprovalSimulation = {
  id: string
  name: string
  description: string | null
  difficulty: string
  industry: string | null
  createdBy: number
  creatorEmail: string
  creatorName: string | null
  organizationId: number | null
  organizationName: string | null
  estimatedDuration: number | null
  createdAt: string
  updatedAt: string
  lastGeneratedAt: string | null
}

export type AdminPendingApprovalsResponse = {
  status: string
  code: number
  data: PendingApprovalSimulation[]
}

export type AdminApprovalActionResponse = {
  status: string
  code: number
  message: string
}

export type AdminSimulationItem = {
  id: string
  name: string
  description: string | null
  stage: "development" | "production"
  status: string
  active: boolean
  difficulty: string | null
  industry: string | null
  simType: string | null
  creatorEmail: string | null
  creatorName: string | null
  organizationName: string | null
  estimatedDuration: number | null
  createdAt: string
  updatedAt: string
}

export type AdminSimulationsResponse = {
  status: string
  code: number
  data: AdminSimulationItem[]
}

export type AdminSimulationsQueryParams = {
  stage?: string
  search?: string
}

// Organization Report Types
export type OrganizationReport = {
  id: number
  organizationName: string
  status: number | null
  createdOn: string | null
  userCount: number
  activeSimulations: number
  completedSimulations: number
  teamSimulations: number
  subscriptionStatus: string | null
  isManuallyPaid: boolean
  adminTags: string | null
  adminNotes: string | null
  archivedAt: string | null
  archivedBy: number | null
}

export type AdminOrganizationReportsResponse = {
  status: string
  code: number
  data: OrganizationReport[]
}

// Organization Management Types
export type UpdateOrgMetadataRequest = {
  organizationId: number
  adminTags?: string
  adminNotes?: string
}

export type UpdateOrgMetadataResponse = {
  status: string
  code: number
  data: {
    success: boolean
    message: string
    organization?: {
      id: number
      organizationName: string
      adminTags: string | null
      adminNotes: string | null
    }
  }
}

export type ArchiveOrgRequest = {
  organizationId: number
  archive: boolean
}

export type ArchiveOrgResponse = {
  status: string
  code: number
  data: {
    success: boolean
    message: string
    organization?: {
      id: number
      organizationName: string
      archivedAt: string | null
      archivedBy: number | null
    }
  }
}

export type SetManuallyPaidRequest = {
  organizationId: number
  isManuallyPaid: boolean
}

export type SetManuallyPaidResponse = {
  status: string
  code: number
  data: {
    organizationId: number
    isManuallyPaid: boolean
    message: string
  }
}

export type GetManuallyPaidResponse = {
  status: string
  code: number
  data: {
    isManuallyPaid: boolean | null
  }
}

export type OrgDeletionImpact = {
  organization: {
    id: number
    organizationName: string
    createdOn: string | null
  }
  impact: {
    userCount: number
    assignedTestsCount: number
    completedSimulationsCount: number
    teamSimulationsCount: number
    rawScoresCount: number
    userNotesCount: number
    organizationMeasuresCount: number
    subscriptionsCount: number
    groupsCount: number
  }
  warnings: string[]
}

export type OrgDeletionImpactResponse = {
  status: string
  code: number
  data: OrgDeletionImpact
}

export type DeleteOrgRequest = {
  organizationId: number
  confirmationName: string
}

export type DeleteOrgResponse = {
  status: string
  code: number
  data: {
    success: boolean
    message: string
    deletedCounts?: {
      users: number
      rawScores: number
      userNotes: number
      organizationMeasures: number
      organizationGroups: number
      testAssignments: number
      subscriptions: number
    }
  }
}

// Organization Report Details Types (for expanded view)
export type OrgReportUserSimulationScore = {
  measureId: number
  measureName: string
  measureCode: string | null
  score: number
}

export type OrgReportUserSimulation = {
  assignmentId: number
  testId: number
  testTitle: string | null
  testShortName: string | null
  status: "not_started" | "in_progress" | "completed"
  dateAssigned: string | null
  dateUserStart: string | null
  dateUserEnd: string | null
  scores: OrgReportUserSimulationScore[]
}

export type OrgReportUser = {
  id: number
  email: string
  firstName: string | null
  lastName: string | null
  role: string | null
  createdAt: string | null
  lastLogin: string | null
  simulations: OrgReportUserSimulation[]
  summary: {
    totalAssigned: number
    completed: number
    inProgress: number
    averageScore: number | null
  }
}

export type OrgReportDetailsData = {
  organization: {
    id: number
    organizationName: string
    status: number
    createdOn: string
    subscriptionStatus: string | null
  }
  users: OrgReportUser[]
  summary: {
    totalUsers: number
    totalSimulationsAssigned: number
    totalSimulationsCompleted: number
    totalSimulationsInProgress: number
    overallAverageScore: number | null
  }
}

export type OrgReportDetailsResponse = {
  status: string
  code: number
  data: OrgReportDetailsData
}

// Simulation Report Types
export type SimulationReport = {
  id: number
  title: string
  shortName: string | null
  active: string
  totalAssignments: number
  completedAssignments: number
  activeAssignments: number
  averageCompletionMinutes: number | null
  organizationUsage: { orgId: number; orgName: string; count: number }[]
}

export type AdminSimulationReportsResponse = {
  status: string
  code: number
  data: SimulationReport[]
}

// Simulation Report with Measures Types
export type MeasureScore = {
  measureId: number
  measureName: string
  measureCode: string | null
  averageScore: number | null
  minScore: number | null
  maxScore: number | null
  scoreCount: number
}

export type SimulationReportWithMeasures = SimulationReport & {
  measureScores: MeasureScore[]
}

export type AdminSimulationReportsWithMeasuresResponse = {
  status: string
  code: number
  data: SimulationReportWithMeasures[]
}

// User Simulation Scores Types
export type UserSimulationScore = {
  measureId: number
  measureName: string
  measureCode: string | null
  score: number
}

export type UserSimulationScoresData = {
  runId: number
  userId: number
  userEmail: string
  userFirstName: string | null
  userLastName: string | null
  testId: number
  testTitle: string
  organizationId: number
  organizationName: string
  dateCompleted: string | null
  measureScores: UserSimulationScore[]
}

export type UserSimulationScoresResponse = {
  status: string
  code: number
  data: UserSimulationScoresData
}

// Measure Types
export type MeasureData = {
  id: number
  measureName: string
  measureDescription: string | null
  dimension: string | null
  isActive: boolean
  organizationCount: number
  assessmentCount: number
}

export type AdminMeasuresResponse = {
  status: string
  code: number
  data: MeasureData[]
}

// Data Explorer Types
export type DataExplorerResult = {
  users: { id: number; email: string; firstName: string | null; lastName: string | null; organizationName: string | null }[]
  simulations: { id: number; title: string; shortName: string | null }[]
  organizations: { id: number; organizationName: string; userCount: number }[]
}

export type AdminDataExplorerResponse = {
  status: string
  code: number
  data: DataExplorerResult
}

// Analytics Types
export type AnalyticsData = {
  dailyActiveUsers: { date: string; count: number }[]
  simulationsPerDay: { date: string; started: number; completed: number }[]
  topSimulations: { id: number; title: string; completions: number }[]
  topOrganizations: { id: number; name: string; activeUsers: number }[]
  userGrowth: { date: string; totalUsers: number }[]
}

export type AdminAnalyticsResponse = {
  status: string
  code: number
  data: AnalyticsData
}

// Comprehensive Analytics Types
export type ComprehensiveAnalytics = {
  summary: {
    totalSimulationsRun: number
    totalCompletedSimulations: number
    totalUsers: number
    totalOrganizations: number
    averageCompletionRate: number
    averageScore: number | null
  }
  activityOverTime: {
    month: string
    started: number
    completed: number
  }[]
  organizationBreakdown: {
    id: number
    name: string
    totalSimulations: number
    completedSimulations: number
    completionRate: number
    userCount: number
    averageScore: number | null
  }[]
  simulationBreakdown: {
    id: number
    title: string
    shortName: string | null
    totalRuns: number
    completedRuns: number
    completionRate: number
    averageScore: number | null
    organizationsUsing: number
  }[]
  scoreDistribution: {
    range: string
    count: number
  }[]
  measurePerformance: {
    measureId: number
    measureName: string
    measureCode: string | null
    averageScore: number
    totalScores: number
  }[]
  recentCompletions: {
    id: number
    userName: string
    userEmail: string
    organizationName: string | null
    simulationTitle: string
    completedAt: string
    score: number | null
  }[]
}

export type ComprehensiveAnalyticsResponse = {
  status: string
  code: number
  data: ComprehensiveAnalytics
}

export type ComprehensiveAnalyticsFilters = {
  organizationId?: number
  simulationId?: number
  startDate?: string
  endDate?: string
}

// Organization Measure Access Types
export type OrganizationForMeasures = {
  id: number
  organizationName: string
  measureCount: number
}

export type OrganizationsForMeasuresResponse = {
  status: string
  code: number
  data: OrganizationForMeasures[]
}

export type MeasureWithOrgAccess = {
  id: number
  measureName: string
  measureDescription: string | null
  category: string | null
  isActive: boolean
  organizationsWithAccess: number
}

export type MeasuresWithAccessResponse = {
  status: string
  code: number
  data: MeasureWithOrgAccess[]
}

export type OrganizationMeasureItem = {
  id: number
  measureName: string
  measureDescription: string | null
  isActive: boolean
  hasAccess: boolean
  organizationMeasureId: number | null
}

export type OrganizationMeasureAccess = {
  organizationId: number
  organizationName: string
  measures: OrganizationMeasureItem[]
}

export type OrganizationMeasureAccessResponse = {
  status: string
  code: number
  data: OrganizationMeasureAccess
}

export type UpdateMeasureAccessRequest = {
  organizationId: number
  measureId: number
  grantAccess: boolean
}

export type UpdateMeasureAccessResponse = {
  status: string
  code: number
  data: {
    success: boolean
    message: string
  }
}

export type BulkUpdateMeasureAccessRequest = {
  organizationId: number
  measureIds: number[]
  grantAccess: boolean
}

export type BulkUpdateMeasureAccessResponse = {
  status: string
  code: number
  data: {
    success: boolean
    message: string
    updatedCount: number
  }
}

// All Simulations Types (for super admin)
export type AllSimulationItem = {
  id: number
  userId: number
  userEmail: string
  userFirstName: string | null
  userLastName: string | null
  organizationId: number
  organizationName: string
  testId: number
  testTitle: string
  testShortName: string | null
  testImgUrl: string | null
  dateAssigned: string | null
  dateUserStart: string | null
  dateUserEnd: string | null
  status: "not_started" | "in_progress" | "completed"
}

export type AllSimulationsData = {
  simulations: AllSimulationItem[]
  total: number
  limit: number
  offset: number
}

export type AllSimulationsResponse = {
  status: string
  code: number
  data: AllSimulationsData
}

export type AllSimulationsQueryParams = {
  limit?: number
  offset?: number
  status?: "all" | "active" | "completed" | "not_started"
  organizationId?: number
  search?: string
}

export type PendingResultsApproval = {
  id: number
  organizationName: string
  allUsersCompletedAt: string | null
  resultsApprovalRequestedAt: string | null
  resultsApprovalStatus: "pending" | "approved" | "rejected" | null
  completedUserCount: number
  totalUserCount: number
}

export type PendingResultsApprovalsRequest = {
  includeAll?: boolean
}

export type PendingResultsApprovalsResponse = {
  status: string
  code: number
  data: PendingResultsApproval[]
}

export type ApproveOrganizationResultsRequest = {
  organizationId: number
  approve: boolean
}

export type ApproveOrganizationResultsResponse = {
  status: string
  code: number
  data: {
    success: boolean
    message: string
  }
}

export type OrganizationResultsAccessStatus = {
  hasAccess: boolean
  status: "pending" | "approved" | "rejected" | null
  message?: string
}

export type OrganizationResultsAccessStatusResponse = {
  status: string
  code: number
  data: OrganizationResultsAccessStatus
}

// Chat message types
export type ChatMessage = {
  id: string
  roomId: string
  senderUserId: number
  senderName: string
  message: string
  messageType: string
  recipientIds: number[] | null
  createdAt: string
}

export type SaveChatMessageRequest = {
  roomId: string
  senderUserId: number
  senderName: string
  message: string
  messageType?: string
  recipientIds?: number[]
}

export type SaveChatMessageResponse = {
  status: string
  code: number
  message: string
  data: ChatMessage
}

export type GetChatMessagesResponse = {
  status: string
  code: number
  data: ChatMessage[]
}

// Team Simulation Types for Super Admin
export type TeamSimulationParticipant = {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  userId: number | null
  status: string
  invitedAt: string
  joinedAt: string | null
  leftAt: string | null
}

export type TeamSimulationChoice = {
  id: string
  choiceId: string
  choiceText: string
  choiceType: string
  votingStatus: string
  votesFor: number
  votesAgainst: number
  votesAbstain: number
  requiredVotes: number
  proposedAt: string
  resolvedAt: string | null
  selectedBySimulation: boolean
  proposedByEmail: string | null
}

export type TeamSimulationCollabRoom = {
  id: string
  name: string
  status: string
  startedAt: string | null
  endedAt: string | null
  choices: TeamSimulationChoice[]
}

export type AdminTeamSimulationItem = {
  id: string
  name: string
  scenarioId: number
  scenarioName: string | null
  organizationId: number
  organizationName: string
  createdBy: number
  createdByEmail: string | null
  createdByName: string | null
  passcode: string
  status: string
  scheduledStartTime: string | null
  timezone: string | null
  createdAt: string
  startedAt: string | null
  endedAt: string | null
  participantCount: number
  participants: TeamSimulationParticipant[]
  collaborationRoom: TeamSimulationCollabRoom | null
}

export type AdminTeamSimulationsData = {
  teamSimulations: AdminTeamSimulationItem[]
  total: number
  limit: number
  offset: number
}

export type AdminTeamSimulationsResponse = {
  status: string
  code: number
  data: AdminTeamSimulationsData
}

export type AdminTeamSimulationsQueryParams = {
  limit?: number
  offset?: number
  status?: "all" | "pending" | "active" | "completed" | "cancelled"
  organizationId?: number
  search?: string
}

// Detailed Team Simulation Types
export type TeamSimulationChoiceVote = {
  participantId: string
  participantEmail: string
  decision: string
  castedAt: string
}

export type TeamSimulationChoiceDetailed = {
  id: string
  choiceId: string
  choiceText: string
  choiceType: string
  votingStatus: string
  votesFor: number
  votesAgainst: number
  votesAbstain: number
  requiredVotes: number
  proposedAt: string
  resolvedAt: string | null
  selectedBySimulation: boolean
  proposedBy: {
    id: string
    email: string
    firstName: string | null
    lastName: string | null
  } | null
  votes: TeamSimulationChoiceVote[]
}

export type TeamSimulationCollabRoomDetailed = {
  id: string
  name: string
  status: string
  votingThreshold: string
  votingTimeLimit: number
  maxParticipants: number
  currentParticipants: number
  startedAt: string | null
  endedAt: string | null
}

export type TeamSimulationChatMessage = {
  id: string
  senderUserId: number
  senderName: string
  message: string
  messageType: string
  recipientIds: number[] | null
  createdAt: string
}

export type TeamSimulationDetailsData = {
  teamSimulation: {
    id: string
    name: string
    scenarioId: number
    scenarioName: string | null
    organizationId: number
    organizationName: string
    createdBy: number
    createdByEmail: string | null
    passcode: string
    status: string
    scheduledStartTime: string | null
    timezone: string | null
    createdAt: string
    startedAt: string | null
    endedAt: string | null
  }
  participants: TeamSimulationParticipant[]
  collaborationRoom: TeamSimulationCollabRoomDetailed | null
  choices: TeamSimulationChoiceDetailed[]
  chatMessages: TeamSimulationChatMessage[]
}

export type TeamSimulationDetailsResponse = {
  status: string
  code: number
  data: TeamSimulationDetailsData
}

// Run Log types
export type RunLogEntry = {
  id: number
  runId: number
  logId: number
  type: string
  icon: string | null
  msgTitle: string | null
  msgDescription: string | null
  msgDateTime: string
  elapsedTime: string
  responseText: string | null
  categoryId: number | null
  planId: number | null
  eventId: number | null
  categoryLevel1Id: number | null
  categoryLevel2Id: number | null
  categoryLevel3Id: number | null
  categoryLevel4Id: number | null
  categoryLevel5Id: number | null
  B4: string | null
  B5: string | null
  B6: string | null
  B7: string | null
}

export type SimulationRunLogsData = {
  runId: number
  simulation: {
    name: string
    testId: number
  }
  user: {
    id: number
    email: string
    name: string
  }
  totalEntries: number
  entriesByType: Record<string, number>
  entries: RunLogEntry[]
}

export type SimulationRunLogsResponse = {
  status: string
  code: number
  data: SimulationRunLogsData
}

export type UserSimulationRun = {
  runId: number
  testId: number
  simulationName: string
  dateAssigned: string
  dateCompleted: string | null
  dateUserStart: string | null
  dateUserEnd: string | null
  hasRunLogs: boolean
  runLogCount: number
  hasUserResults: boolean // Indicates userResults JSON exists (fallback source)
}

export type UserSimulationRunsData = {
  userId: number
  runs: UserSimulationRun[]
}

export type UserSimulationRunsResponse = {
  status: string
  code: number
  data: UserSimulationRunsData
}

// User Microlearning Data types (for View Raw Data page)
export type UserMicrolearningJournalEntry = {
  id: number
  promptText: string
  measureId: number
  measureName: string
  response: string
  createdOn: string
}

export type UserMicrolearningScenarioCompletion = {
  id: number
  scenarioId: number
  measureId: number
  measureName: string
  selectedChoice: string
  isCorrect: boolean
  score: number
  completedDate: string
}

export type UserMicrolearningWeeklyResponse = {
  id: number
  weekNumber: number
  monthNumber: number
  measureId: number
  measureName: string
  prompt: string
  response: string
  score: number | null
  createdOn: string
}

export type UserMicrolearningDataResponse = {
  status: string
  code: number
  data: {
    userId: number
    journalEntries: UserMicrolearningJournalEntry[]
    scenarioCompletions: UserMicrolearningScenarioCompletion[]
    weeklyResponses: UserMicrolearningWeeklyResponse[]
    stats: {
      totalJournalEntries: number
      totalScenariosCompleted: number
      scenarioAccuracy: number
      totalWeeklyResponses: number
    }
  }
}

// Data Explorer detailed types
export type DataExplorerUserScore = {
  measureId: number
  measureName: string
  measureCode: string | null
  score: number
}

export type DataExplorerUserSimulation = {
  id: number
  testId: number
  testTitle: string | null
  testShortName: string | null
  status: string
  dateAssigned: string | null
  dateUserStart: string | null
  dateUserEnd: string | null
  scores: DataExplorerUserScore[]
}

export type DataExplorerUserMicrolearning = {
  id: number
  measureId: number
  measureName: string
  status: string
  createdAt: string
  completedAt: string | null
}

export type DataExplorerUserDetailsData = {
  user: {
    id: number
    email: string
    firstName: string | null
    lastName: string | null
    organizationId: number | null
    organizationName: string | null
    createdAt: string | null
    lastLogin: string | null
    role: string | null
  }
  assignedSimulations: DataExplorerUserSimulation[]
  microlearning: DataExplorerUserMicrolearning[]
  summary: {
    totalSimulations: number
    completedSimulations: number
    averageScore: number | null
    microlearningEnrolled: number
    microlearningCompleted: number
  }
}

export type DataExplorerUserDetailsResponse = {
  status: string
  code: number
  data: DataExplorerUserDetailsData
}

export type DataExplorerOrgUser = {
  id: number
  email: string
  firstName: string | null
  lastName: string | null
  role: string | null
  simulationsCompleted: number
  lastActive: string | null
}

export type DataExplorerOrgSimulation = {
  testId: number
  testTitle: string | null
  testShortName: string | null
  totalAssigned: number
  completed: number
  inProgress: number
}

export type DataExplorerOrgMeasure = {
  measureId: number
  measureName: string
  isActive: boolean
}

export type DataExplorerOrgAvailableSim = {
  testId: number
  testTitle: string | null
  testShortName: string | null
}

export type DataExplorerOrgDetailsData = {
  organization: {
    id: number
    organizationName: string
    createdAt: string | null
    onboardingCompleted: boolean
    subscriptionStatus: string | null
  }
  users: DataExplorerOrgUser[]
  assignedSimulations: DataExplorerOrgSimulation[]
  measures: DataExplorerOrgMeasure[]
  availableSimulations: DataExplorerOrgAvailableSim[]
  summary: {
    totalUsers: number
    activeUsers: number
    totalSimulationsAssigned: number
    totalSimulationsCompleted: number
    measuresTracked: number
  }
}

export type DataExplorerOrgDetailsResponse = {
  status: string
  code: number
  data: DataExplorerOrgDetailsData
}

export type AssignSimulationRequest = {
  userId: number
  testId: number
}

export type AssignSimulationResponse = {
  status: string
  code: number
  data: {
    success: boolean
    assignmentId?: number
    message: string
  }
}

// Organization action types for Data Explorer
export type UnlockMeasureRequest = {
  organizationId: number
  measureId: number
}

export type UnlockMeasureResponse = {
  status: string
  code: number
  data: {
    success: boolean
    message: string
  }
}

export type AssignSimulationToOrgRequest = {
  organizationId: number
  testId: number
}

export type AssignSimulationToOrgResponse = {
  status: string
  code: number
  data: {
    success: boolean
    message: string
  }
}

export type AvailableMeasure = {
  id: number
  measureName: string
  measureCode: string | null
}

export type AvailableMeasuresResponse = {
  status: string
  code: number
  data: {
    measures: AvailableMeasure[]
  }
}

export type AvailableSimulation = {
  id: number
  title: string | null
  shortName: string | null
}

export type AvailableSimulationsResponse = {
  status: string
  code: number
  data: {
    simulations: AvailableSimulation[]
  }
}

// Test Session Types
export type CreateTestSessionRequest = {
  name: string
  simulationId: string
  simulationType: "V2_SIMULATION" | "LEGACY_TEST"
  testerEmails?: string[]
}

export type CreateTestSessionResponse = {
  status: string
  code: number
  message: string
  testSessionId: string
  passcode: string
  shareableLink: string
  expiresAt: string
}

export type TestSessionLoginRequest = {
  email: string
  passcode: string
  testSessionId: string
}

export type SimulationPageRow = {
  id: number
  title: string
  dateImported: string
  contents: string
  manualDescription: string
}

export type SimulationPageData = {
  status: string
  code: number
  message: string
  data: SimulationPageRow[]
}

export type TestSessionLoginResponse = {
  status: string
  code: number
  message: string
  token: string
  testSessionId: string
  simulationType: "V2_SIMULATION" | "LEGACY_TEST"
  simulationData: SimulationPageData
  userData: {
    id: number
    participantId: string
    firstName: string | null
    lastName: string | null
    email: string
  }
}

export type TestSessionParticipant = {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  joinedAt: string | null
}

export type TestSessionRunSummary = {
  id: string
  runNumber: number
  startedAt: string
  completedAt: string | null
  elapsedSeconds: number
}

export type TestSessionDetails = {
  id: string
  name: string
  status: "pending" | "active" | "completed" | "expired"
  type: "V2_SIMULATION" | "LEGACY_TEST"
  expiresAt: string
  createdAt: string
  participants: TestSessionParticipant[]
  runs: TestSessionRunSummary[]
  v2Simulation: {
    id: string
    name: string
    description: string | null
  } | null
}

export type TestSessionDetailsResponse = {
  status: string
  code: number
  message: string
  data: TestSessionDetails
}

export type TestSessionListItem = {
  id: string
  name: string
  status: "pending" | "active" | "completed" | "expired"
  type: "V2_SIMULATION" | "LEGACY_TEST"
  expiresAt: string
  createdAt: string
  participantCount: number
  runCount: number
}

export type TestSessionListResponse = {
  status: string
  code: number
  data: TestSessionListItem[]
}

export type StartTestRunResponse = {
  status: string
  code: number
  message: string
  runId: string
  runNumber: number
  startedAt: string
}

export type TestSessionRunWithDetails = {
  id: string
  runNumber: number
  isTestRun: boolean
  startedAt: string
  completedAt: string | null
  elapsedSeconds: number
  savePointData: Record<string, unknown> | null
  finalResultData: Record<string, unknown> | null
  participant: {
    id: string
    email: string
    firstName: string | null
    lastName: string | null
  }
}

export type TestSessionRunsResponse = {
  status: string
  code: number
  data: TestSessionRunWithDetails[]
}

// Test Session Results Types (for Quick Test Results section)
export type CompletedTestRun = {
  id: string
  runNumber: number
  participantEmail: string
  participantName: string | null
  startedAt: string
  completedAt: string
  elapsedSeconds: number
  finalResultData: Record<string, unknown> | null
}

export type SimulationTestSession = {
  id: string
  name: string
  status: "pending" | "active" | "completed" | "expired"
  createdAt: string
  expiresAt: string
  participantCount: number
  completedRuns: CompletedTestRun[]
  totalRuns: number
}

export type SimulationTestSessionsResponse = {
  status: string
  code: number
  data: SimulationTestSession[]
}

export const meseeknaApiV2 = meseeknaApi.injectEndpoints({
  endpoints: (build) => ({
    loginUserPlain: build.mutation<LoginPlainResponse, LoginPlainRequest>({
      query: (credentials) => ({
        url: "/saas/users/login-plain",
        method: "POST",
        body: credentials,
      }),
    }),
    getUserProfile: build.query<UserProfileResponse, void>({
      query: () => ({
        url: "/saas/users/profile",
        method: "GET",
      }),
    }),
    updateOnboardingAppointment: build.mutation<
      UpdateOnboardingAppointmentResponse,
      UpdateOnboardingAppointmentRequest
    >({
      query: (data) => ({
        url: "/saas/subscriptions/update-onboarding-appointment",
        method: "POST",
        body: data,
      }),
    }),
    getOnboardingCompletion: build.query<
      OnboardingCompletionResponse,
      string | number
    >({
      query: (organizationId) => ({
        url: `/saas/organizations/${organizationId}/onboarding-completion`,
        method: "GET",
      }),
    }),
    updateOnboardingCompletion: build.mutation<
      OnboardingCompletionResponse,
      { organizationId: string | number; completed: boolean }
    >({
      query: ({ organizationId, completed }) => ({
        url: `/saas/organizations/${organizationId}/onboarding-completion`,
        method: "PUT",
        body: { onboardingCompleted: completed },
      }),
    }),
    updateSelectedScenario: build.mutation<
      UpdateSelectedScenarioResponse,
      { organizationId: string | number; selectedScenario: string }
    >({
      query: ({ organizationId, selectedScenario }) => ({
        url: `/saas/organizations/${organizationId}/selected-scenario`,
        method: "PUT",
        body: { selectedScenario },
      }),
    }),
    // Get all measures (for onboarding measure selection)
    getMeasures: build.query<MeasuresResponse, void>({
      query: () => ({
        url: "/saas/measures",
        method: "GET",
      }),
    }),
    // Get all measures with option to include inactive and counts (for admin)
    getAllMeasures: build.query<MeasuresWithCountsResponse, GetAllMeasuresParams>({
      query: (params) => ({
        url: "/saas/measures",
        method: "GET",
        params: {
          includeInactive: params.includeInactive ? "true" : "false",
          withCounts: params.withCounts ? "true" : "false",
        },
      }),
    }),
    // Soft delete a measure (set isActive to false)
    softDeleteMeasure: build.mutation<SoftDeleteMeasureResponse, SoftDeleteMeasureRequest>({
      query: ({ measureId }) => ({
        url: `/saas/measures/${measureId}`,
        method: "DELETE",
      }),
    }),
    // Restore a soft-deleted measure
    restoreMeasure: build.mutation<RestoreMeasureResponse, RestoreMeasureRequest>({
      query: ({ measureId }) => ({
        url: `/saas/measures/${measureId}/restore`,
        method: "POST",
      }),
    }),
    getOrganizationMeasures: build.query<
      GetOrganizationMeasuresResponse,
      number
    >({
      query: (organizationId) => ({
        url: `/saas/organization-measures/organization/${organizationId}`,
        method: "GET",
      }),
    }),
    createOrganizationMeasure: build.mutation<
      CreateOrganizationMeasureResponse,
      { organizationId: number } & CreateOrganizationMeasureRequest
    >({
      query: ({ organizationId, ...data }) => ({
        url: `/saas/organization-measures/organization/${organizationId}`,
        method: "POST",
        body: data,
      }),
    }),
    updateOrganizationMeasure: build.mutation<
      UpdateOrganizationMeasureResponse,
      { id: number } & UpdateOrganizationMeasureRequest
    >({
      query: ({ id, ...data }) => ({
        url: `/saas/organization-measures/${id}`,
        method: "PUT",
        body: data,
      }),
    }),
    deleteOrganizationMeasure: build.mutation<
      DeleteOrganizationMeasureResponse,
      number
    >({
      query: (id) => ({
        url: `/saas/organization-measures/${id}`,
        method: "DELETE",
      }),
    }),
    changePassword: build.mutation<
      ChangePasswordResponse,
      ChangePasswordRequest
    >({
      query: (credentials) => ({
        url: "/saas/users/change-password",
        method: "POST",
        body: credentials,
      }),
    }),
    createOrganizationUser: build.mutation<
      CreateUserResponse,
      CreateUserRequest
    >({
      query: (userData) => ({
        url: "/saas/users/create-organization-user",
        method: "POST",
        body: userData,
      }),
    }),
    // CSV Import - uses v2 endpoint that properly sends invitation emails via Postmark
    csvImportV2: build.mutation<CsvImportResponse, FormData>({
      query: (formData) => ({
        url: "/saas/users/csv-import",
        method: "POST",
        body: formData,
      }),
    }),
    // Bulk Reminder - sends simulation reminder emails via Postmark, or deactivates users
    bulkReminderV2: build.mutation<
      {
        status: string
        code: number
        message: string
        data: {
          sent?: number
          failed?: number
          skipped?: number
          deactivatedCount?: number
          details?: {
            sent: Array<{ userId: number; email: string }>
            failed: Array<{ userId: number; email: string; reason: string }>
            skipped: Array<{ userId: number; reason: string }>
          }
        }
      },
      { userIds: number[]; status: string }
    >({
      query: (data) => ({
        url: "/saas/users/bulk-reminder",
        method: "POST",
        body: data,
      }),
    }),
    completeOnboarding: build.mutation<
      { code: number; message: string },
      { token: string; password: string; email: string }
    >({
      query: (credentials) => ({
        url: "/saas/users/complete-onboarding",
        method: "POST",
        body: credentials,
      }),
    }),
    // New password reset endpoints
    forgotPassword: build.mutation<
      ForgotPasswordResponse,
      ForgotPasswordRequest
    >({
      query: (data) => ({
        url: "/saas/users/forgot-password",
        method: "POST",
        body: data,
      }),
    }),
    resetPassword: build.mutation<ResetPasswordResponse, ResetPasswordRequest>({
      query: (data) => ({
        url: "/saas/users/reset-password",
        method: "POST",
        body: data,
      }),
    }),
    refreshSession: build.mutation<RefreshSessionResponse, void>({
      query: () => ({
        url: "/saas/users/auth/refresh",
        method: "POST",
      }),
    }),
    adminCreateUser: build.mutation<
      AdminCreateUserResponse,
      AdminCreateUserRequest
    >({
      query: (userData) => ({
        url: "/saas/users/admin",
        method: "POST",
        body: userData,
      }),
    }),
    getAdminOrganizations: build.query<AdminOrganizationsResponse, void>({
      query: () => ({
        url: "/saas/organizations",
        method: "GET",
      }),
    }),
    getAdminTests: build.query<AdminTestsResponse, void>({
      query: () => ({
        url: "/saas/tests",
        method: "GET",
      }),
    }),
    // Full tests CRUD endpoints
    listTests: build.query<
      AdminTestsResponse,
      { includeContents?: boolean; active?: string; simType?: string } | void
    >({
      query: (params) => ({
        url: "/saas/tests",
        method: "GET",
        params: params || {},
      }),
    }),
    getTest: build.query<AdminTestResponse, number>({
      query: (id) => ({
        url: `/saas/tests/${id}`,
        method: "GET",
      }),
    }),
    createTest: build.mutation<AdminTestResponse, CreateTestRequest>({
      query: (data) => ({
        url: "/saas/tests",
        method: "POST",
        body: data,
      }),
    }),
    updateTest: build.mutation<
      AdminTestResponse,
      { id: number; data: UpdateTestRequest }
    >({
      query: ({ id, data }) => ({
        url: `/saas/tests/${id}`,
        method: "PUT",
        body: data,
      }),
    }),
    deleteTest: build.mutation<DeleteTestResponse, number>({
      query: (id) => ({
        url: `/saas/tests/${id}`,
        method: "DELETE",
      }),
    }),
    updateTestImage: build.mutation<
      AdminTestResponse,
      { id: number; imageData: string | null }
    >({
      query: ({ id, imageData }) => ({
        url: `/saas/tests/${id}/image`,
        method: "PUT",
        body: { imageData },
      }),
    }),
    assignSimulationToOrganization: build.mutation<
      SimulationAssignmentResponse,
      { orgId: number; testId: number; body: SimulationAssignmentRequest }
    >({
      query: ({ orgId, testId, body }) => ({
        url: `/saas/organizations/${orgId}/simulations/${testId}`,
        method: "POST",
        body,
      }),
    }),
    removeSimulationFromOrganization: build.mutation<
      { status: string; code: number; message: string },
      { orgId: number; testId: number }
    >({
      query: ({ orgId, testId }) => ({
        url: `/saas/organizations/${orgId}/simulations/${testId}`,
        method: "DELETE",
      }),
    }),
    createOrganization: build.mutation<
      CreateOrganizationResponse,
      CreateOrganizationRequest
    >({
      query: (data) => ({
        url: "/saas/organizations",
        method: "POST",
        body: data,
      }),
    }),
    updateOrganization: build.mutation<
      UpdateOrganizationResponse,
      { id: number; data: UpdateOrganizationRequest }
    >({
      query: ({ id, data }) => ({
        url: `/saas/organizations/${id}`,
        method: "PUT",
        body: data,
      }),
    }),
    getOrganizationSimulations: build.query<
      OrganizationSimulationsResponse,
      number
    >({
      query: (orgId) => ({
        url: `/saas/organizations/${orgId}/simulations`,
        method: "GET",
      }),
    }),
    getUserAssignments: build.query<
      {
        status: string
        code: number
        data: { testId: number; runId: string }[]
      },
      number
    >({
      query: (userId) => ({
        url: `/saas/users/${userId}/assignments`,
        method: "GET",
      }),
    }),
    // Get users by organization (for team simulation participant selection)
    getUsersByOrganization: build.query<
      {
        status: string
        code: number
        data: Array<{
          id: number
          firstName: string
          lastName: string
          email: string
          role: number
          organizationId: number
          roleName: string
        }>
      },
      number
    >({
      query: (organizationId) => ({
        url: `/saas/users/organization/${organizationId}`,
        method: "GET",
      }),
    }),
    // Team simulation endpoints
    createTeamSimulation: build.mutation<
      CreateTeamSimulationResponse,
      CreateTeamSimulationRequest
    >({
      query: (teamData) => ({
        url: "/saas/team-simulations/create",
        method: "POST",
        body: teamData,
      }),
    }),
    loginWithPasscode: build.mutation<
      LoginWithPasscodeResponse,
      LoginWithPasscodeRequest
    >({
      query: (loginData) => ({
        url: "/saas/team-simulations/login",
        method: "POST",
        body: loginData,
      }),
    }),
    sendTeamInvitations: build.mutation<
      SendTeamInvitationsResponse,
      SendTeamInvitationsRequest
    >({
      query: (invitationData) => ({
        url: "/saas/team-simulations/send-invitations",
        method: "POST",
        body: invitationData,
      }),
    }),
    autoStartTeamSimulation: build.mutation<
      AutoStartTeamSimulationResponse,
      { teamSimulationId: string }
    >({
      query: ({ teamSimulationId }) => ({
        url: "/saas/team-simulations/auto-start",
        method: "POST",
        body: { teamSimulationId },
      }),
    }),
    getTeamSimulationDetails: build.query<
      GetTeamSimulationDetailsResponse,
      { teamSimulationId: string }
    >({
      query: ({ teamSimulationId }) => ({
        url: `/saas/team-simulations/${teamSimulationId}`,
        method: "GET",
      }),
    }),
    // Composite Score endpoints
    getCompositeScoreSets: build.query<CompositeScoreSetsResponse, void>({
      query: () => ({
        url: "/adminapi/composite/all/getAllSetsWithTiers",
        method: "GET",
      }),
    }),
    getCompositeScoreTiers: build.query<CompositeScoreTiersResponse, number>({
      query: (setId) => ({
        url: `/adminapi/composite/tiers/${setId}`,
        method: "GET",
      }),
    }),
    getCompositeScoreBuckets: build.query<CompositeScoreBucketsResponse, number>({
      query: (tierId) => ({
        url: `/adminapi/composite/tier/bucket/${tierId}`,
        method: "GET",
      }),
    }),
    updateCompositeScoreBucket: build.mutation<
      { status: string; code: number; message: string },
      UpdateCompositeScoreBucketRequest
    >({
      query: (body) => ({
        url: "/adminapi/composite/bucket/update",
        method: "POST",
        body,
      }),
    }),
    getOrgsByScoreSetId: build.query<CompositeScoreOrgMappingsResponse, number>({
      query: (setId) => ({
        url: `/adminapi/composite/set/getOrgsBySetId/${setId}`,
        method: "GET",
      }),
    }),
    // Score Set CRUD mutations
    createCompositeScoreSet: build.mutation<
      { status: string; code: string; message: string; data: { insertId: number } },
      CreateCompositeScoreSetRequest
    >({
      query: (body) => ({
        url: "/adminapi/composite/add",
        method: "POST",
        body,
      }),
    }),
    updateCompositeScoreSet: build.mutation<
      { status: string; code: string; message: string },
      UpdateCompositeScoreSetRequest
    >({
      query: (body) => ({
        url: "/adminapi/composite/update",
        method: "PUT",
        body,
      }),
    }),
    deleteCompositeScoreSet: build.mutation<
      { status: string; code: string; message: string },
      number
    >({
      query: (id) => ({
        url: `/adminapi/composite/delete/${id}`,
        method: "DELETE",
      }),
    }),
    // Tier CRUD mutations
    createCompositeScoreTier: build.mutation<
      { status: string; code: string; message: string; data: { insertId: number } },
      CreateCompositeScoreTierRequest
    >({
      query: (body) => ({
        url: "/adminapi/composite/tier/add",
        method: "POST",
        body,
      }),
    }),
    updateCompositeScoreTier: build.mutation<
      { status: string; code: string; message: string },
      UpdateCompositeScoreTierRequest
    >({
      query: (body) => ({
        url: "/adminapi/composite/tier/update",
        method: "PUT",
        body,
      }),
    }),
    deleteCompositeScoreTier: build.mutation<
      { status: string; code: string; message: string },
      number
    >({
      query: (id) => ({
        url: `/adminapi/composite/tier/delete/${id}`,
        method: "DELETE",
      }),
    }),
    // Bucket CRUD mutations
    createCompositeScoreBucket: build.mutation<
      { status: string; code: string; message: string; data: { insertId: number } },
      CreateCompositeScoreBucketRequest
    >({
      query: (body) => ({
        url: "/adminapi/composite/bucket/add",
        method: "POST",
        body,
      }),
    }),
    deleteCompositeScoreBucket: build.mutation<
      { status: string; code: string; message: string },
      number
    >({
      query: (id) => ({
        url: `/adminapi/composite/bucket/delete/${id}`,
        method: "DELETE",
      }),
    }),
    // Org Mapping mutation
    updateCompositeScoreOrgMapping: build.mutation<
      { status: string; code: string; message: string },
      UpdateCompositeScoreOrgMappingRequest
    >({
      query: (body) => ({
        url: "/adminapi/composite/compositeOrgMappingUpdate",
        method: "POST",
        body,
      }),
    }),
    getAdminUsers: build.query<AdminUsersResponse, void>({
      query: () => ({
        url: "/saas/users",
        method: "GET",
      }),
    }),
    updateAdminUser: build.mutation<
      AdminUpdateUserResponse,
      AdminUpdateUserRequest
    >({
      query: ({ id, ...body }) => ({
        url: `/saas/admin/dashboard/user/${id}`,
        method: "PUT",
        body,
      }),
    }),
    updateAdminUserPassword: build.mutation<
      AdminUpdateUserPasswordResponse,
      AdminUpdateUserPasswordRequest
    >({
      query: ({ userId, newPassword }) => ({
        url: `/saas/admin/dashboard/user/${userId}/update-password`,
        method: "POST",
        body: { newPassword },
      }),
    }),
    // Admin Dashboard endpoints
    getAdminOverviewStats: build.query<AdminOverviewStatsResponse, void>({
      query: () => ({
        url: "/saas/admin/dashboard/overview",
        method: "GET",
      }),
    }),
    getAdminLiveSummary: build.query<AdminLiveSummaryResponse, void>({
      query: () => ({
        url: "/saas/admin/dashboard/live/summary",
        method: "GET",
      }),
    }),
    getAdminActiveIndividualSimulations: build.query<
      AdminActiveIndividualSimulationsResponse,
      void
    >({
      query: () => ({
        url: "/saas/admin/dashboard/live/individual-simulations",
        method: "GET",
      }),
    }),
    getAdminActiveTeamSessions: build.query<
      AdminActiveTeamSessionsResponse,
      void
    >({
      query: () => ({
        url: "/saas/admin/dashboard/live/team-sessions",
        method: "GET",
      }),
    }),
    getAdminRecentActivity: build.query<
      AdminRecentActivityResponse,
      { limit?: number }
    >({
      query: ({ limit } = {}) => ({
        url: `/saas/admin/dashboard/live/recent-activity${limit ? `?limit=${limit}` : ""}`,
        method: "GET",
      }),
    }),
    // All simulations listing (legacy + custom)
    getAdminSimulations: build.query<AdminSimulationsResponse, AdminSimulationsQueryParams | void>({
      query: (params) => ({
        url: "/saas/admin/dashboard/simulations",
        method: "GET",
        params: params || undefined,
      }),
    }),
    // Pending Approvals endpoints
    getAdminPendingApprovals: build.query<AdminPendingApprovalsResponse, void>({
      query: () => ({
        url: "/saas/admin/dashboard/pending-approvals",
        method: "GET",
      }),
    }),
    approveSimulation: build.mutation<AdminApprovalActionResponse, string>({
      query: (id) => ({
        url: `/saas/admin/dashboard/pending-approvals/${id}/approve`,
        method: "POST",
      }),
    }),
    rejectSimulation: build.mutation<AdminApprovalActionResponse, string>({
      query: (id) => ({
        url: `/saas/admin/dashboard/pending-approvals/${id}/reject`,
        method: "POST",
      }),
    }),
    // Organization Reports endpoint
    getAdminOrganizationReports: build.query<AdminOrganizationReportsResponse, { includeArchived?: boolean; archivedOnly?: boolean } | void>({
      query: (params) => {
        const queryParams = new URLSearchParams()
        if (params?.includeArchived) queryParams.set("includeArchived", "true")
        if (params?.archivedOnly) queryParams.set("archivedOnly", "true")
        const queryString = queryParams.toString()
        return {
          url: `/saas/admin/dashboard/organization-reports${queryString ? `?${queryString}` : ""}`,
          method: "GET",
        }
      },
    }),
    // Organization Report Details endpoint (for expanded view with users, sims, scores)
    getOrgReportDetails: build.query<OrgReportDetailsResponse, number>({
      query: (organizationId) => ({
        url: `/saas/admin/dashboard/organization-reports/${organizationId}`,
        method: "GET",
      }),
    }),
    // Organization Management endpoints
    updateOrgMetadata: build.mutation<UpdateOrgMetadataResponse, UpdateOrgMetadataRequest>({
      query: ({ organizationId, ...body }) => ({
        url: `/saas/admin/dashboard/organization/${organizationId}/metadata`,
        method: "PUT",
        body,
      }),
    }),
    archiveOrganization: build.mutation<ArchiveOrgResponse, ArchiveOrgRequest>({
      query: ({ organizationId, archive }) => ({
        url: `/saas/admin/dashboard/organization/${organizationId}/archive`,
        method: "POST",
        body: { archive },
      }),
    }),
    getManuallyPaidStatus: build.query<GetManuallyPaidResponse, number>({
      query: (organizationId) => ({
        url: `/saas/admin/dashboard/organization/${organizationId}/manually-paid`,
        method: "GET",
      }),
    }),
    setManuallyPaidStatus: build.mutation<SetManuallyPaidResponse, SetManuallyPaidRequest>({
      query: ({ organizationId, isManuallyPaid }) => ({
        url: `/saas/admin/dashboard/organization/${organizationId}/manually-paid`,
        method: "POST",
        body: { isManuallyPaid },
      }),
    }),
    getOrgDeletionImpact: build.query<OrgDeletionImpactResponse, number>({
      query: (organizationId) => ({
        url: `/saas/admin/dashboard/organization/${organizationId}/deletion-impact`,
        method: "GET",
      }),
    }),
    deleteOrganization: build.mutation<DeleteOrgResponse, DeleteOrgRequest>({
      query: ({ organizationId, confirmationName }) => ({
        url: `/saas/admin/dashboard/organization/${organizationId}`,
        method: "DELETE",
        body: { confirmationName },
      }),
    }),
    // Simulation Reports endpoint
    getAdminSimulationReports: build.query<AdminSimulationReportsResponse, void>({
      query: () => ({
        url: "/saas/admin/dashboard/simulation-reports",
        method: "GET",
      }),
    }),
    // Simulation Reports with Measures endpoint
    getAdminSimulationReportsWithMeasures: build.query<AdminSimulationReportsWithMeasuresResponse, void>({
      query: () => ({
        url: "/saas/admin/dashboard/simulation-reports-with-measures",
        method: "GET",
      }),
    }),
    // User Simulation Scores endpoint
    getUserSimulationScores: build.query<UserSimulationScoresResponse, number>({
      query: (runId) => ({
        url: `/saas/admin/dashboard/simulation-scores/${runId}`,
        method: "GET",
      }),
    }),
    // Measures endpoint
    getAdminMeasures: build.query<AdminMeasuresResponse, void>({
      query: () => ({
        url: "/saas/admin/dashboard/measures",
        method: "GET",
      }),
    }),
    // Data Explorer endpoint
    getAdminDataExplorer: build.query<
      AdminDataExplorerResponse,
      { q?: string; type?: "users" | "simulations" | "organizations" | "all" }
    >({
      query: ({ q = "", type = "all" } = {}) => ({
        url: `/saas/admin/dashboard/data-explorer?q=${encodeURIComponent(q)}&type=${type}`,
        method: "GET",
      }),
    }),
    // Data Explorer User Details endpoint
    getDataExplorerUserDetails: build.query<DataExplorerUserDetailsResponse, number>({
      query: (userId) => ({
        url: `/saas/admin/dashboard/data-explorer/user/${userId}`,
        method: "GET",
      }),
    }),
    // Data Explorer Organization Details endpoint
    getDataExplorerOrgDetails: build.query<DataExplorerOrgDetailsResponse, number>({
      query: (organizationId) => ({
        url: `/saas/admin/dashboard/data-explorer/organization/${organizationId}`,
        method: "GET",
      }),
    }),
    // Assign Simulation to User endpoint
    assignSimulationToUser: build.mutation<AssignSimulationResponse, AssignSimulationRequest>({
      query: (data) => ({
        url: "/saas/admin/dashboard/data-explorer/assign-simulation",
        method: "POST",
        body: data,
      }),
    }),
    // Unlock Measure for Organization endpoint
    unlockMeasureForOrg: build.mutation<UnlockMeasureResponse, UnlockMeasureRequest>({
      query: (data) => ({
        url: "/saas/admin/dashboard/data-explorer/unlock-measure",
        method: "POST",
        body: data,
      }),
    }),
    // Assign Simulation to Organization endpoint
    assignSimulationToOrg: build.mutation<AssignSimulationToOrgResponse, AssignSimulationToOrgRequest>({
      query: (data) => ({
        url: "/saas/admin/dashboard/data-explorer/assign-simulation-to-org",
        method: "POST",
        body: data,
      }),
    }),
    // Get available measures for unlocking
    getAvailableMeasures: build.query<AvailableMeasuresResponse, void>({
      query: () => ({
        url: "/saas/admin/dashboard/data-explorer/available-measures",
        method: "GET",
      }),
    }),
    // Get available simulations for assigning
    getAvailableSimulations: build.query<AvailableSimulationsResponse, void>({
      query: () => ({
        url: "/saas/admin/dashboard/data-explorer/available-simulations",
        method: "GET",
      }),
    }),
    // Analytics endpoint
    getAdminAnalytics: build.query<AdminAnalyticsResponse, { days?: number; organizationId?: number }>({
      query: ({ days = 30, organizationId } = {}) => {
        let url = `/saas/admin/dashboard/analytics?days=${days}`
        if (organizationId) {
          url += `&organizationId=${organizationId}`
        }
        return { url, method: "GET" }
      },
    }),
    // Comprehensive Analytics endpoint
    getComprehensiveAnalytics: build.query<ComprehensiveAnalyticsResponse, ComprehensiveAnalyticsFilters | void>({
      query: (filters) => {
        const params = new URLSearchParams()
        if (filters?.organizationId) params.append("organizationId", filters.organizationId.toString())
        if (filters?.simulationId) params.append("simulationId", filters.simulationId.toString())
        if (filters?.startDate) params.append("startDate", filters.startDate)
        if (filters?.endDate) params.append("endDate", filters.endDate)
        const queryString = params.toString()
        return {
          url: `/saas/admin/dashboard/comprehensive-analytics${queryString ? `?${queryString}` : ""}`,
          method: "GET",
        }
      },
    }),
    // Organization Measure Access endpoints
    getOrganizationsForMeasures: build.query<OrganizationsForMeasuresResponse, void>({
      query: () => ({
        url: "/saas/admin/dashboard/organizations-for-measures",
        method: "GET",
      }),
    }),
    getMeasuresWithAccess: build.query<MeasuresWithAccessResponse, void>({
      query: () => ({
        url: "/saas/admin/dashboard/measures-with-access",
        method: "GET",
      }),
    }),
    getOrganizationMeasureAccess: build.query<
      OrganizationMeasureAccessResponse,
      number
    >({
      query: (orgId) => ({
        url: `/saas/admin/dashboard/organization/${orgId}/measures`,
        method: "GET",
      }),
    }),
    updateOrganizationMeasureAccess: build.mutation<
      UpdateMeasureAccessResponse,
      UpdateMeasureAccessRequest
    >({
      query: ({ organizationId, measureId, grantAccess }) => ({
        url: `/saas/admin/dashboard/organization/${organizationId}/measures/${measureId}`,
        method: "POST",
        body: { grantAccess },
      }),
    }),
    bulkUpdateOrganizationMeasureAccess: build.mutation<
      BulkUpdateMeasureAccessResponse,
      BulkUpdateMeasureAccessRequest
    >({
      query: ({ organizationId, measureIds, grantAccess }) => ({
        url: `/saas/admin/dashboard/organization/${organizationId}/measures/bulk`,
        method: "POST",
        body: { measureIds, grantAccess },
      }),
    }),
    // All Simulations endpoint (for super admin)
    getAllSimulations: build.query<
      AllSimulationsResponse,
      AllSimulationsQueryParams | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams()
        if (params?.limit) searchParams.set("limit", params.limit.toString())
        if (params?.offset) searchParams.set("offset", params.offset.toString())
        if (params?.status) searchParams.set("status", params.status)
        if (params?.organizationId) searchParams.set("organizationId", params.organizationId.toString())
        if (params?.search) searchParams.set("search", params.search)
        const queryString = searchParams.toString()
        return {
          url: `/saas/admin/dashboard/all-simulations${queryString ? `?${queryString}` : ""}`,
          method: "GET",
        }
      },
    }),
    // Results Approval endpoints
    getPendingResultsApprovals: build.query<
      PendingResultsApprovalsResponse,
      PendingResultsApprovalsRequest | void
    >({
      query: (params) => ({
        url: `/saas/admin/dashboard/pending-results-approvals${params?.includeAll ? "?includeAll=true" : ""}`,
        method: "GET",
      }),
    }),
    approveOrganizationResults: build.mutation<
      ApproveOrganizationResultsResponse,
      ApproveOrganizationResultsRequest
    >({
      query: ({ organizationId, approve }) => ({
        url: `/saas/admin/dashboard/organization/${organizationId}/approve-results`,
        method: "POST",
        body: { approve },
      }),
    }),
    getOrganizationResultsAccessStatus: build.query<
      OrganizationResultsAccessStatusResponse,
      number
    >({
      query: (organizationId) => ({
        url: `/saas/admin/dashboard/organization/${organizationId}/results-access-status`,
        method: "GET",
      }),
    }),
    // Organization measure scores (accessible to recruiter admins)
    getOrganizationMeasureScores: build.query<
      {
        status: string
        code: number
        data: Array<{
          measureId: number
          measureName: string
          measureCode: string | null
          averageScore: number
          minScore: number
          maxScore: number
          scoreCount: number
        }>
      },
      number
    >({
      query: (organizationId) => ({
        url: `/saas/admin/dashboard/organization/${organizationId}/measure-scores`,
        method: "GET",
      }),
    }),
    // Per-user measure scores for an organization (accessible to recruiter admins)
    getOrganizationUserMeasureScores: build.query<
      {
        status: string
        code: number
        data: Array<{
          userId: number
          email: string
          firstName: string | null
          lastName: string | null
          runId: number
          measures: Array<{
            measureId: number
            measureName: string
            measureCode: string | null
            measureColor: string | null
            score: number
          }>
        }>
      },
      number
    >({
      query: (organizationId) => ({
        url: `/saas/admin/dashboard/organization/${organizationId}/user-measure-scores`,
        method: "GET",
      }),
    }),
    // Chat message endpoints
    saveChatMessage: build.mutation<
      SaveChatMessageResponse,
      SaveChatMessageRequest
    >({
      query: (messageData) => ({
        url: `/saas/collaboration/rooms/${messageData.roomId}/messages`,
        method: "POST",
        body: messageData,
      }),
    }),
    getChatMessages: build.query<
      GetChatMessagesResponse,
      { roomId: string; limit?: number; before?: string }
    >({
      query: ({ roomId, limit, before }) => ({
        url: `/saas/collaboration/rooms/${roomId}/messages`,
        method: "GET",
        params: { limit, before },
      }),
    }),
    // Team Simulations for Super Admin
    getAdminTeamSimulations: build.query<
      AdminTeamSimulationsResponse,
      AdminTeamSimulationsQueryParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams()
        if (params?.limit) queryParams.append("limit", params.limit.toString())
        if (params?.offset) queryParams.append("offset", params.offset.toString())
        if (params?.status) queryParams.append("status", params.status)
        if (params?.organizationId) queryParams.append("organizationId", params.organizationId.toString())
        if (params?.search) queryParams.append("search", params.search)
        const queryString = queryParams.toString()
        return {
          url: `/saas/admin/dashboard/team-simulations${queryString ? `?${queryString}` : ""}`,
          method: "GET",
        }
      },
    }),
    getAdminTeamSimulationDetails: build.query<
      TeamSimulationDetailsResponse,
      string
    >({
      query: (teamSimulationId) => ({
        url: `/saas/admin/dashboard/team-simulations/${teamSimulationId}`,
        method: "GET",
      }),
    }),
    // Run Log endpoints
    getUserSimulationRuns: build.query<UserSimulationRunsResponse, number>({
      query: (userId) => ({
        url: `/saas/admin/dashboard/user/${userId}/simulation-runs`,
        method: "GET",
      }),
    }),
    getSimulationRunLogs: build.query<SimulationRunLogsResponse, number>({
      query: (runId) => ({
        url: `/saas/admin/dashboard/simulation-run-logs/${runId}`,
        method: "GET",
      }),
    }),
    getUserMicrolearningData: build.query<UserMicrolearningDataResponse, number>({
      query: (userId) => ({
        url: `/saas/admin/dashboard/user/${userId}/microlearning-data`,
        method: "GET",
      }),
    }),
    // Support endpoints
    sendSupportMessage: build.mutation<
      { success: boolean; message: string },
      { subject: string; message: string; userEmail: string; userName: string }
    >({
      query: (body) => ({
        url: "/saas/support/message",
        method: "POST",
        body,
      }),
    }),
    // Team Simulation Checkout endpoint
    createTeamSimulationCheckout: build.mutation<
      { status: string; code: number; checkoutUrl: string },
      {
        scenarioId: number
        name: string
        participantEmails: string[]
        organizationId: number
        createdBy?: number
        successUrl?: string
        cancelUrl?: string
      }
    >({
      query: (body) => ({
        url: "/saas/team-simulations/create-checkout",
        method: "POST",
        body,
      }),
    }),
    // Test Session endpoints
    createTestSession: build.mutation<
      CreateTestSessionResponse,
      CreateTestSessionRequest
    >({
      query: (data) => ({
        url: "/saas/test-sessions/create",
        method: "POST",
        body: data,
      }),
    }),
    loginTestSession: build.mutation<
      TestSessionLoginResponse,
      TestSessionLoginRequest
    >({
      query: (data) => ({
        url: "/saas/test-sessions/login",
        method: "POST",
        body: data,
      }),
    }),
    getTestSessionDetails: build.query<TestSessionDetailsResponse, string>({
      query: (testSessionId) => ({
        url: `/saas/test-sessions/${testSessionId}`,
        method: "GET",
      }),
    }),
    getMyTestSessions: build.query<TestSessionListResponse, void>({
      query: () => ({
        url: "/saas/test-sessions/my-sessions",
        method: "GET",
      }),
    }),
    getTestSessionRuns: build.query<TestSessionRunsResponse, string>({
      query: (testSessionId) => ({
        url: `/saas/test-sessions/${testSessionId}/runs`,
        method: "GET",
      }),
    }),
    startTestRun: build.mutation<
      StartTestRunResponse,
      { testSessionId: string; participantId: string }
    >({
      query: ({ testSessionId, participantId }) => ({
        url: `/saas/test-sessions/${testSessionId}/runs/start`,
        method: "POST",
        body: { participantId },
      }),
    }),
    saveTestRunProgress: build.mutation<
      { status: string; code: number; message: string },
      { runId: string; savePointData: Record<string, unknown>; elapsedSeconds: number }
    >({
      query: ({ runId, savePointData, elapsedSeconds }) => ({
        url: `/saas/test-sessions/runs/${runId}/progress`,
        method: "PUT",
        body: { savePointData, elapsedSeconds },
      }),
    }),
    completeTestRun: build.mutation<
      { status: string; code: number; message: string },
      { runId: string; finalResultData: Record<string, unknown>; elapsedSeconds: number }
    >({
      query: ({ runId, finalResultData, elapsedSeconds }) => ({
        url: `/saas/test-sessions/runs/${runId}/complete`,
        method: "PUT",
        body: { finalResultData, elapsedSeconds },
      }),
    }),
    endTestSession: build.mutation<
      { status: string; code: number; message: string; data: { id: string; status: string } },
      string
    >({
      query: (testSessionId) => ({
        url: `/saas/test-sessions/${testSessionId}/end`,
        method: "POST",
      }),
    }),
    deleteTestSession: build.mutation<
      { status: string; code: number; message: string },
      string
    >({
      query: (testSessionId) => ({
        url: `/saas/test-sessions/${testSessionId}`,
        method: "DELETE",
      }),
    }),
    getTestSessionsForSimulation: build.query<
      SimulationTestSessionsResponse,
      string
    >({
      query: (simulationId) => ({
        url: `/saas/test-sessions/simulation/${simulationId}`,
        method: "GET",
      }),
    }),
    getTutorialConfig: build.query<TutorialConfigResponse, number>({
      query: (testId) => ({
        url: `/saas/tests/${testId}/tutorial-config`,
        method: "GET",
      }),
    }),
    updateTutorialConfig: build.mutation<
      TutorialConfigResponse,
      { testId: number; config: unknown }
    >({
      query: ({ testId, config }) => ({
        url: `/saas/tests/${testId}/tutorial-config`,
        method: "PUT",
        body: config,
      }),
    }),
    deleteTutorialConfig: build.mutation<TutorialConfigResponse, number>({
      query: (testId) => ({
        url: `/saas/tests/${testId}/tutorial-config`,
        method: "DELETE",
      }),
    }),
  }),
  overrideExisting: false,
})

export type TutorialConfigResponse = {
  status?: string
  code?: number
  message?: string
  data?: unknown | null
}

export const {
  useLoginUserPlainMutation,
  useChangePasswordMutation,
  useCreateOrganizationUserMutation,
  useCsvImportV2Mutation,
  useBulkReminderV2Mutation,
  useCompleteOnboardingMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useRefreshSessionMutation,
  useGetOnboardingCompletionQuery,
  useUpdateOnboardingCompletionMutation,
  useUpdateSelectedScenarioMutation,
  useGetMeasuresQuery,
  useGetAllMeasuresQuery,
  useSoftDeleteMeasureMutation,
  useRestoreMeasureMutation,
  useGetOrganizationMeasuresQuery,
  useCreateOrganizationMeasureMutation,
  useUpdateOrganizationMeasureMutation,
  useDeleteOrganizationMeasureMutation,
  useUpdateOnboardingAppointmentMutation,
  useGetUserProfileQuery,
  useAdminCreateUserMutation,
  useGetAdminOrganizationsQuery,
  useGetAdminTestsQuery,
  useListTestsQuery,
  useGetTestQuery,
  useCreateTestMutation,
  useUpdateTestMutation,
  useDeleteTestMutation,
  useUpdateTestImageMutation,
  useAssignSimulationToOrganizationMutation,
  useRemoveSimulationFromOrganizationMutation,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useGetOrganizationSimulationsQuery,
  useGetUserAssignmentsQuery,
  useGetUsersByOrganizationQuery,
  useCreateTeamSimulationMutation,
  useLoginWithPasscodeMutation,
  useSendTeamInvitationsMutation,
  useAutoStartTeamSimulationMutation,
  useGetTeamSimulationDetailsQuery,
  // Composite Score hooks
  useGetCompositeScoreSetsQuery,
  useGetCompositeScoreTiersQuery,
  useLazyGetCompositeScoreTiersQuery,
  useGetCompositeScoreBucketsQuery,
  useLazyGetCompositeScoreBucketsQuery,
  useUpdateCompositeScoreBucketMutation,
  useGetOrgsByScoreSetIdQuery,
  useLazyGetOrgsByScoreSetIdQuery,
  // Composite Score CRUD hooks
  useCreateCompositeScoreSetMutation,
  useUpdateCompositeScoreSetMutation,
  useDeleteCompositeScoreSetMutation,
  useCreateCompositeScoreTierMutation,
  useUpdateCompositeScoreTierMutation,
  useDeleteCompositeScoreTierMutation,
  useCreateCompositeScoreBucketMutation,
  useDeleteCompositeScoreBucketMutation,
  useUpdateCompositeScoreOrgMappingMutation,
  useGetAdminUsersQuery,
  useUpdateAdminUserMutation,
  useUpdateAdminUserPasswordMutation,
  // Admin Dashboard hooks
  useGetAdminOverviewStatsQuery,
  useGetAdminLiveSummaryQuery,
  useGetAdminActiveIndividualSimulationsQuery,
  useGetAdminActiveTeamSessionsQuery,
  useGetAdminRecentActivityQuery,
  // New Admin Dashboard hooks
  useGetAdminSimulationsQuery,
  useGetAdminPendingApprovalsQuery,
  useApproveSimulationMutation,
  useRejectSimulationMutation,
  useGetAdminOrganizationReportsQuery,
  useGetOrgReportDetailsQuery,
  useLazyGetOrgReportDetailsQuery,
  useGetAdminSimulationReportsQuery,
  useGetAdminSimulationReportsWithMeasuresQuery,
  useGetAdminMeasuresQuery,
  useGetAdminDataExplorerQuery,
  useGetAdminAnalyticsQuery,
  useGetComprehensiveAnalyticsQuery,
  // Data Explorer Detail hooks
  useGetDataExplorerUserDetailsQuery,
  useLazyGetDataExplorerUserDetailsQuery,
  useGetDataExplorerOrgDetailsQuery,
  useLazyGetDataExplorerOrgDetailsQuery,
  useAssignSimulationToUserMutation,
  // Organization action hooks for Data Explorer
  useUnlockMeasureForOrgMutation,
  useAssignSimulationToOrgMutation,
  useGetAvailableMeasuresQuery,
  useGetAvailableSimulationsQuery,
  // Organization Management hooks
  useUpdateOrgMetadataMutation,
  useArchiveOrganizationMutation,
  useGetManuallyPaidStatusQuery,
  useLazyGetManuallyPaidStatusQuery,
  useSetManuallyPaidStatusMutation,
  useGetOrgDeletionImpactQuery,
  useLazyGetOrgDeletionImpactQuery,
  useDeleteOrganizationMutation,
  // Organization Measure Access hooks
  useGetOrganizationsForMeasuresQuery,
  useGetMeasuresWithAccessQuery,
  useGetOrganizationMeasureAccessQuery,
  useUpdateOrganizationMeasureAccessMutation,
  useBulkUpdateOrganizationMeasureAccessMutation,
  // All Simulations hook (for super admin)
  useGetAllSimulationsQuery,
  // Results Approval hooks
  useGetPendingResultsApprovalsQuery,
  useApproveOrganizationResultsMutation,
  useGetOrganizationResultsAccessStatusQuery,
  useGetOrganizationMeasureScoresQuery,
  useGetOrganizationUserMeasureScoresQuery,
  // User Simulation Scores hook
  useGetUserSimulationScoresQuery,
  useLazyGetUserSimulationScoresQuery,
  // Chat message hooks
  useSaveChatMessageMutation,
  useGetChatMessagesQuery,
  useLazyGetChatMessagesQuery,
  // Team Simulation hooks for Super Admin
  useGetAdminTeamSimulationsQuery,
  useLazyGetAdminTeamSimulationsQuery,
  useGetAdminTeamSimulationDetailsQuery,
  useLazyGetAdminTeamSimulationDetailsQuery,
  // Run Log hooks for Super Admin
  useGetUserSimulationRunsQuery,
  useLazyGetUserSimulationRunsQuery,
  useGetSimulationRunLogsQuery,
  useLazyGetSimulationRunLogsQuery,
  // User Microlearning Data hooks for Super Admin (View Raw Data page)
  useGetUserMicrolearningDataQuery,
  useLazyGetUserMicrolearningDataQuery,
  // Support hooks
  useSendSupportMessageMutation,
  // Team Simulation Checkout hook
  useCreateTeamSimulationCheckoutMutation,
  // Test Session hooks
  useCreateTestSessionMutation,
  useLoginTestSessionMutation,
  useGetTestSessionDetailsQuery,
  useLazyGetTestSessionDetailsQuery,
  useGetMyTestSessionsQuery,
  useGetTestSessionRunsQuery,
  useLazyGetTestSessionRunsQuery,
  useStartTestRunMutation,
  useSaveTestRunProgressMutation,
  useCompleteTestRunMutation,
  useEndTestSessionMutation,
  useDeleteTestSessionMutation,
  useGetTestSessionsForSimulationQuery,
  useLazyGetTestSessionsForSimulationQuery,
  useGetTutorialConfigQuery,
  useLazyGetTutorialConfigQuery,
  useUpdateTutorialConfigMutation,
  useDeleteTutorialConfigMutation,
} = meseeknaApiV2
